/*
 * Vehicle Tracker Frontend Application - Enhanced Version
 * 
 * This is the comprehensive React application for the Vehicle Tracking System.
 * 
 * MAIN FEATURES:
 * - User authentication with email/password
 * - Enhanced vehicle registration with contact information and email notifications
 * - Real-time GPS tracking with large mobile-friendly markers
 * - Interactive map with vehicle layers and detailed tooltips
 * - Route subscription system with location picker and distance settings
 * - Advertisement request system with email notifications
 * - Complete admin panel for managing requests and operations
 * - Click-to-map navigation from all vehicle listings
 * - Location-based default map positioning
 * - PWA capabilities with install functionality
 * 
 * ADMIN PANEL FEATURES:
 * - Admin authentication (separate from user auth)
 * - Vehicle request management with GPS device assignment
 * - Advertisement request approval system
 * - Public bus management
 * - Admin user management
 * - Comprehensive dashboard with statistics
 * 
 * EMAIL NOTIFICATION SYSTEM:
 * - Vehicle registration requests → emails mark@mark-dev.com
 * - Advertisement requests → emails mark@mark-dev.com
 * - Distinct functions for future payment automation
 * 
 * MAP ENHANCEMENTS:
 * - Large, mobile-friendly markers
 * - Separate vehicle layers
 * - Detailed clickable tooltips
 * - Real-time location updates
 * - Selected vehicle highlighting with red markers
 * - Geolocation-based default map center
 * - Auto-locate button for user location
 * - Subscribed buses list with distance calculations
 * - Map filtering with clickable cards
 * 
 * COMPONENT STRUCTURE:
 * - AuthProvider: User authentication context
 * - AdminAuthProvider: Admin authentication context  
 * - AppRouter: Handles routing between user and admin interfaces
 * - OfflineBanner: Shows connectivity status
 * - VehicleTracker: Main user dashboard
 * - AdminPanel: Complete admin management interface
 */

import React, { useState } from 'react';
import './App.css';

// Context providers
import { AuthProvider, AdminAuthProvider } from './contexts/AuthContext';

// Components
import OfflineBanner from './components/shared/OfflineBanner';
import AppRouter from './components/router/AppRouter';
import OAuthCallback from './components/Auth/OAuthCallback';
import EmailVerification from './components/Auth/EmailVerification';



// ============================================================================
// MAIN APP COMPONENT
// ============================================================================

export default function App() {
  // const [authMode, setAuthMode] = useState('user'); // 'user', 'register', 'admin', 'forgot-password'
  // Check for admin access URL and set initial authMode accordingly
  // Normalize trailing slashes so /driver/ and /admin/ work like /driver and /admin
  const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const initialAuthMode = currentPath === '/admin' ? 'admin' : currentPath === '/driver' ? 'driver' : 'user';

  const [authMode, setAuthMode] = useState(initialAuthMode);

  // Handle OAuth callback routes
  // const currentPath = window.location.pathname;

  if (currentPath === '/auth/google/callback') {
    return <OAuthCallback provider="google" />;
  }
  if (currentPath === '/auth/facebook/callback') {
    return <OAuthCallback provider="facebook" />;
  }

  // Handle email verification route
  if (currentPath === '/verify-email') {
    return <EmailVerification />;
  }

  return (
    <AuthProvider>
      <AdminAuthProvider>
        {/* Offline Banner - Shows when no internet connection */}
        <OfflineBanner />
        <AppRouter authMode={authMode} setAuthMode={setAuthMode} />
      </AdminAuthProvider>
    </AuthProvider>
  );
}

// Export useAuth for use in other components
export { useAuth } from './contexts/AuthContext';